import { useEffect, useState } from 'react';
import type { BankAccount, PriceBasis, ReceiptDiffDecision, ReceiptDiffItem, UnpricedIntake, VatOffsetTask, VatSourceKind } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney } from '../format';

interface Props {
  onBack: () => void;
}

const REASON: Record<string, string> = { surplus: 'artıq', shortage: 'əskik', damaged: 'zədəli' };

// Mühasib (KT-62): qaiməsiz qəbulun qiyməti və fərq aktları üzrə qərar.
export function IntakeApprovals({ onBack }: Props) {
  const [diffs, setDiffs] = useState<ReceiptDiffItem[]>([]);
  const [unpriced, setUnpriced] = useState<UnpricedIntake[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [supplierOf, setSupplierOf] = useState<Record<string, string>>({});
  const [paidOf, setPaidOf] = useState<Record<string, string>>({});
  const [basisOf, setBasisOf] = useState<Record<string, PriceBasis>>({});
  const [sourceOf, setSourceOf] = useState<Record<string, VatSourceKind | ''>>({});
  const [sourceNoOf, setSourceNoOf] = useState<Record<string, string>>({});
  const [noteOf, setNoteOf] = useState<Record<string, string>>({});
  const [tasks, setTasks] = useState<VatOffsetTask[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function reload() {
    // ƏDV tapşırıqları yalnız vat.manage ilə (sahib, mühasib); icazə yoxdursa bölmə görünmür
    api.vatTasks().then(setTasks).catch(() => setTasks(null));
    return Promise.all([api.intakeDiffs(), api.intakeUnpriced()])
      .then(([d, u]) => {
        setDiffs(d);
        setUnpriced(u);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }

  useEffect(() => {
    reload();
    api.purchaseOptions().then((o) => setSuppliers(o.suppliers.map((s) => ({ id: s.id, name: s.name })))).catch(() => setSuppliers([]));
    api.transferAccounts().then((a) => {
      setAccounts(a);
      if (a.length > 0) setAccountId(a[0].id);
    }).catch(() => setAccounts([]));
  }, []);

  async function decide(d: ReceiptDiffItem, decision: ReceiptDiffDecision) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.intakeDecide(d.id, decision, noteOf[d.id]?.trim() || null);
      setNotice(`${d.displayNo} · ${d.productName}: qərar yazıldı (${formatMoney(r.amount)})`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qərar yazılmadı');
    } finally {
      setBusy(false);
    }
  }

  async function price(doc: UnpricedIntake) {
    const supplierId = supplierOf[doc.docId];
    if (!supplierId) return setError('Təchizatçı seçin');
    const paid = Number(paidOf[doc.docId]) || 0;
    setBusy(true);
    setError(null);
    try {
      const r = await api.intakePrice(doc.docId, {
        id: crypto.randomUUID(),
        supplierId,
        lines: doc.lines.map((l) => ({ lineId: l.lineId, price: Number(prices[l.lineId]) || 0 })),
        paid,
        moneyAccountId: paid > 0 ? accountId || null : null,
        priceBasis: basisOf[doc.docId] ?? 'gross',
        sourceKind: sourceOf[doc.docId] || null,
        sourceNo: sourceNoOf[doc.docId]?.trim() || null,
        sourceDate: null,
        note: noteOf[doc.docId]?.trim() || null,
      });
      setNotice(`${doc.displayNo}: ${r.displayNo} — cəm ${formatMoney(r.total)}, borc ${formatMoney(r.debt)}`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qiymət yazılmadı');
    } finally {
      setBusy(false);
    }
  }

  async function resolveTask(t: VatOffsetTask) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.vatResolve(t.id);
      setNotice(`${t.purchaseDisplayNo}: ${formatMoney(t.amount)} ƏDV mayaya köçürüldü (${r.displayNo})`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tapşırıq həll olunmadı');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Qəbul təsdiqi</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>
      <main className="content">
        <section className="card card-wide">
          <h2>Qəbul təsdiqi</h2>
          {notice && <p className="ok" aria-live="polite">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}

          <h3>Fərq aktları</h3>
          {diffs.length === 0 && <p className="muted">Fərq aktı yoxdur.</p>}
          <ul className="product-list">
            {diffs.map((d) => (
              <li key={d.id} className="product-row">
                <span className="product-name">
                  {d.supplierName} · {d.productName}
                </span>
                <span className="muted small">
                  {d.displayNo} · gözlənilən {d.expected}, sayılan {d.received}
                  {d.damaged > 0 ? `, zədəli ${d.damaged}` : ''} · {REASON[d.reason]} · {formatMoney(d.amount)}
                </span>
                <input aria-label={`${d.displayNo} qeyd`} placeholder="Qeyd" maxLength={500} value={noteOf[d.id] ?? ''} onChange={(e) => setNoteOf((cur) => ({ ...cur, [d.id]: e.target.value }))} disabled={busy} />
                {d.reason === 'surplus' ? (
                  <button type="button" className="btn-small" onClick={() => decide(d, 'accept_surplus')} disabled={busy}>
                    Artığı qəbul et
                  </button>
                ) : (
                  <button type="button" className="btn-small" onClick={() => decide(d, 'reduce_debt')} disabled={busy}>
                    Borcu azalt
                  </button>
                )}
                <button type="button" className="link-btn" onClick={() => decide(d, 'claim')} disabled={busy}>
                  İddia
                </button>
              </li>
            ))}
          </ul>

          {tasks !== null && (
            <>
              <h3>ƏDV: mayaya köçürmə tapşırıqları</h3>
              {tasks.filter((t) => !t.resolved).length === 0 && <p className="muted">Açıq tapşırıq yoxdur.</p>}
              <ul className="product-list">
                {tasks
                  .filter((t) => !t.resolved)
                  .map((t) => (
                    <li key={t.id} className="product-row">
                      <span className="product-name">
                        {t.purchaseDisplayNo} · {t.supplierName}
                      </span>
                      <span className="muted small">
                        {t.reason === 'cash_payment' ? 'nağd ödəniş' : 'ƏDV depozitə köçürülməyib'} · {formatMoney(t.amount)} ƏDV əvəzləşdirilə bilməz
                      </span>
                      <button type="button" className="btn-small" onClick={() => resolveTask(t)} disabled={busy}>
                        Mayaya köçür
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          )}

          <h3>Qiymətsiz qəbullar</h3>
          {unpriced.length === 0 && <p className="muted">Qiymətsiz qəbul yoxdur.</p>}
          {unpriced.map((doc) => (
            <div key={doc.docId} className="card">
              <p>
                <strong>{doc.displayNo}</strong> · {doc.warehouseName} · {doc.date}
              </p>
              <div className="form-grid">
                <label>
                  Təchizatçı
                  <select value={supplierOf[doc.docId] ?? ''} onChange={(e) => setSupplierOf((cur) => ({ ...cur, [doc.docId]: e.target.value }))} disabled={busy}>
                    <option value="">Seçin</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Qiymət əsası
                  <select value={basisOf[doc.docId] ?? 'gross'} onChange={(e) => setBasisOf((cur) => ({ ...cur, [doc.docId]: e.target.value as PriceBasis }))} disabled={busy}>
                    <option value="gross">ƏDV daxil</option>
                    <option value="net">ƏDV üstünə</option>
                  </select>
                </label>
                <label>
                  Mənbə
                  <select value={sourceOf[doc.docId] ?? ''} onChange={(e) => setSourceOf((cur) => ({ ...cur, [doc.docId]: e.target.value as VatSourceKind | '' }))} disabled={busy}>
                    <option value="">Göstərilməyib</option>
                    <option value="e_invoice">E-qaimə</option>
                    <option value="customs">Gömrük bəyannaməsi</option>
                    <option value="e_purchase_act">Elektron alış aktı</option>
                    <option value="paper_invoice">Kağız qaimə</option>
                    <option value="receipt">Çek</option>
                  </select>
                </label>
                <label>
                  Mənbənin nömrəsi
                  <input type="text" maxLength={60} value={sourceNoOf[doc.docId] ?? ''} onChange={(e) => setSourceNoOf((cur) => ({ ...cur, [doc.docId]: e.target.value }))} disabled={busy} />
                </label>
                <label>
                  Ödənilən (bankdan)
                  <input type="number" min="0" step="0.01" value={paidOf[doc.docId] ?? '0'} onChange={(e) => setPaidOf((cur) => ({ ...cur, [doc.docId]: e.target.value }))} disabled={busy} />
                </label>
                <label>
                  Bank hesabı
                  <select value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={busy || accounts.length === 0}>
                    {accounts.length === 0 && <option value="">Hesab yoxdur</option>}
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <ul className="product-list">
                {doc.lines.map((l) => (
                  <li key={l.lineId} className="product-row">
                    <span className="product-name">{l.productName}</span>
                    <span className="muted small">
                      {l.qty} {l.unit}
                    </span>
                    <input type="number" min="0" step="0.01" aria-label={`${l.productName} qiymət`} value={prices[l.lineId] ?? ''} onChange={(e) => setPrices((cur) => ({ ...cur, [l.lineId]: e.target.value }))} disabled={busy} />
                  </li>
                ))}
              </ul>
              <label>
                Qeyd
                <input aria-label={`${doc.displayNo} qeyd`} maxLength={500} value={noteOf[doc.docId] ?? ''} onChange={(e) => setNoteOf((cur) => ({ ...cur, [doc.docId]: e.target.value }))} disabled={busy} />
              </label>
              <button type="button" className="btn-primary" onClick={() => price(doc)} disabled={busy}>
                Qiyməti yaz
              </button>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
